<template>
	<div class="create-auto-campaign-wrapper">
		<div class="create-auto-campaign-page">
			<div class="create-campaign-container container">
				<!-- Header -->
				<div class="page-header d-flex align-items-center my-4">
					<router-link :to="{ name: 'autoCampaigns' }" class="back-btn me-3" title="Back to Auto Campaigns">
						<i class="fad fa-arrow-left"></i>
					</router-link>
					<div>
						<h2 class="page-title">Create Auto Campaign</h2>
						<p class="page-subtitle">Choose the type of automated campaign you want to create.</p>
					</div>
				</div>

				<!-- Campaign Type Selection -->
				<div class="row">
					<div class="col-md-6">
						<div class="campaign-type-card" :class="{ 'selected': selectedType === 'triggers' }" @click="selectType('triggers')">
							<div class="card h-100">
								<div class="card-body d-flex flex-column text-center">
									<div class="campaign-icon mb-3">
										<i class="fad fa-bolt fa-4x text-warning"></i>
									</div>
									<h3 class="card-title">Create Auto Campaign with Triggers</h3>
									<p class="card-text flex-grow-1">
										Set up campaigns that automatically send emails, letters, or postcards when specific triggers are activated. Perfect for event-based marketing and follow-ups.
									</p>
									<div class="features-list text-start">
										<div class="feature-item">
											<i class="fad fa-check-circle text-success me-2"></i>
											Event-based triggers
										</div>
										<div class="feature-item">
											<i class="fad fa-check-circle text-success me-2"></i>
											Multiple campaign types
										</div>
										<div class="feature-item">
											<i class="fad fa-check-circle text-success me-2"></i>
											Advanced targeting
										</div>
									</div>
									<div class="selection-indicator mt-3">
										<div class="form-check">
											<input 
												class="form-check-input" 
												type="radio" 
												name="campaignType" 
												id="triggersOption" 
												value="triggers"
												v-model="selectedType"
											>
											<label class="form-check-label" for="triggersOption">
												Select this option
											</label>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="col-md-6">
						<div class="campaign-type-card" :class="{ 'selected': selectedType === 'sequences' }" @click="selectType('sequences')">
							<div class="card h-100">
								<div class="card-body d-flex flex-column text-center">
									<div class="campaign-icon mb-3">
										<i class="fad fa-list-ol fa-4x text-primary"></i>
									</div>
									<h3 class="card-title">Create Auto Campaign with Sequences</h3>
									<p class="card-text flex-grow-1">
										Build automated email sequences that send a series of messages over time. Ideal for nurturing leads, onboarding, and drip campaigns.
									</p>
									<div class="features-list text-start">
										<div class="feature-item">
											<i class="fad fa-check-circle text-success me-2"></i>
											Time-based sequences
										</div>
										<div class="feature-item">
											<i class="fad fa-check-circle text-success me-2"></i>
											Drip campaigns
										</div>
										<div class="feature-item">
											<i class="fad fa-check-circle text-success me-2"></i>
											Lead nurturing flows
										</div>
									</div>
									<div class="selection-indicator mt-3">
										<div class="form-check">
											<input 
												class="form-check-input" 
												type="radio" 
												name="campaignType" 
												id="sequencesOption" 
												value="sequences"
												v-model="selectedType"
											>
											<label class="form-check-label" for="sequencesOption">
												Select this option
											</label>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Next Button -->
				<div class="row mt-4">
					<div class="col-12 text-center">
						<button 
							type="button" 
							class="btn btn-primary btn-lg px-5"
							:disabled="!selectedType"
							@click="proceedToNext"
						>
							Next
							<i class="fad fa-arrow-right ms-2"></i>
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Sequences Campaign Creation Modal -->
		<div class="modal fade" id="sequencesCampaignModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="fad fa-list-ol text-primary me-2"></i>
							Create Campaign with Sequences
						</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModal"></button>
					</div>
					<div class="modal-body">
						<form @submit.prevent="createSequencesCampaign">
							<!-- Campaign Basic Info -->
							<div class="section-header mb-4">
								<h6 class="section-title">Campaign Information</h6>
							</div>
							
							<div class="row mb-3">
								<div class="col-12">
									<label class="form-label" for="campaignName">Campaign Name <span class="text-danger">*</span></label>
									<input 
										type="text" 
										class="form-control" 
										id="campaignName"
										v-model="sequencesCampaign.name"
										placeholder="Enter campaign name"
										required
									>
								</div>
							</div>
							
							<div class="row mb-4">
								<div class="col-12">
									<label class="form-label" for="campaignDescription">Campaign Description</label>
									<textarea 
										class="form-control" 
										id="campaignDescription"
										v-model="sequencesCampaign.description"
										placeholder="Describe your sequences campaign"
										rows="3"
									></textarea>
								</div>
							</div>

							<div class="row mb-4">
								<div class="col-12">
									<label class="form-label" for="campaignUtm">UTM Campaign</label>
									<utm-campaign-input id="campaignUtm" v-model="sequencesCampaign.utm_campaign" />
								</div>
							</div>

							<!-- Data Lists Selection -->
							<div class="section-header mb-3">
								<h6 class="section-title">Select Data Lists</h6>
								<p class="section-subtitle">Choose which municipality lists to include in this campaign</p>
							</div>

							<!-- Search Data Lists -->
							<div class="search-wrapper mb-3">
								<div class="search-input-wrapper">
									<i class="fad fa-search search-icon"></i>
									<input 
										type="text" 
										class="search-input" 
										placeholder="Search data lists"
										v-model="dataListSearchTerm"
										@input="filterDataLists"
									>
								</div>
							</div>

							<!-- Data Lists Loading -->
							<div v-if="loadingDataLists" class="text-center py-4">
								<div class="spinner-border text-primary" role="status">
									<span class="visually-hidden">Loading data lists...</span>
								</div>
								<p class="text-muted mt-2">Loading data lists...</p>
							</div>

							<!-- Data Lists Selection -->
							<div v-else-if="filteredDataLists.length > 0" class="data-lists-selection">
								<div class="select-all-container mb-3">
									<div class="form-check">
										<input 
											class="form-check-input" 
											type="checkbox" 
											id="selectAllLists"
											:checked="allListsSelected"
											@change="toggleSelectAllLists"
										>
										<label class="form-check-label" for="selectAllLists">
											Select all data lists ({{ filteredDataLists.length }})
										</label>
									</div>
								</div>

								<div class="data-lists-grid">
									<div 
										v-for="dataList in filteredDataLists" 
										:key="dataList.id" 
										class="data-list-item"
										:class="{ 'selected': isDataListSelected(dataList.id) }"
									>
										<div class="form-check">
											<input 
												class="form-check-input" 
												type="checkbox" 
												:id="`dataList${dataList.id}`"
												:value="dataList.id"
												v-model="sequencesCampaign.selectedDataLists"
											>
											<label class="form-check-label" :for="`dataList${dataList.id}`">
												<div class="data-list-card">
													<div class="data-list-icon">
														<i class="fad fa-list"></i>
													</div>
													<div class="data-list-info">
														<div class="data-list-name">{{ dataList.name }}</div>
														<div class="data-list-meta">
															<span class="data-list-count">{{ dataList.items_count || 0 }} items</span>
															<span v-if="dataList.description" class="data-list-description">{{ dataList.description }}</span>
														</div>
													</div>
												</div>
											</label>
										</div>
									</div>
								</div>
							</div>

							<!-- No Data Lists -->
							<div v-else class="no-data-lists text-center py-4">
								<i class="fad fa-database fa-3x text-muted mb-3"></i>
								<h6 class="text-muted">No data lists found</h6>
								<p class="text-muted">{{ dataListSearchTerm ? 'No lists match your search.' : 'No data lists are available.' }}</p>
							</div>
						</form>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModal">Cancel</button>
						<button 
							type="button" 
							class="btn btn-primary"
							@click="createSequencesCampaign"
							:disabled="!canCreateCampaign || creatingCampaign"
						>
							<div v-if="creatingCampaign" class="spinner-border spinner-border-sm me-2" role="status">
								<span class="visually-hidden">Creating...</span>
							</div>
							{{ creatingCampaign ? 'Creating...' : 'Create Campaign' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import restAPI from '../../api/index'
import { Modal } from 'bootstrap'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'
import { normalizeUtmCampaign } from '@/utils/utm'

export default {
	name: 'CreateAutoCampaign',
	components: {
		UtmCampaignInput,
	},
	data() {
		return {
			selectedType: null,
			// Sequences campaign modal data
			sequencesCampaign: {
				name: '',
				description: '',
				utm_campaign: '',
				selectedDataLists: []
			},
			// Data lists
			dataLists: [],
			filteredDataLists: [],
			dataListSearchTerm: '',
			loadingDataLists: false,
			creatingCampaign: false
		}
	},
	computed: {
		allListsSelected() {
			return this.filteredDataLists.length > 0 && 
				this.sequencesCampaign.selectedDataLists.length === this.filteredDataLists.length
		},
		canCreateCampaign() {
			return this.sequencesCampaign.name.trim().length > 0 && 
				this.sequencesCampaign.selectedDataLists.length > 0
		}
	},
	methods: {
		selectType(type) {
			this.selectedType = type;
		},
		async proceedToNext() {
			if (!this.selectedType) return;
			
			if (this.selectedType === 'triggers') {
				this.$router.push({ name: 'createAutoCampaignWithTriggers' });
			} else if (this.selectedType === 'sequences') {
				// Show modal for sequences
				await this.loadDataLists();
				this.$nextTick(() => {
					const modalElement = document.getElementById('sequencesCampaignModal');
					if (modalElement) {
						const modal = new Modal(modalElement);
						modal.show();
					}
				});
			}
		},
		async loadDataLists() {
			try {
				this.loadingDataLists = true;
				const response = await restAPI.get('campaign-data-lists');
				this.dataLists = response.data || [];
				this.filteredDataLists = [...this.dataLists];
			} catch (error) {
				console.error('Error loading data lists:', error);
				this.$swal.fire({
					title: 'Error',
					text: 'Failed to load data lists. Please try again.',
					icon: 'error'
				});
			} finally {
				this.loadingDataLists = false;
			}
		},
		filterDataLists() {
			const searchTerm = this.dataListSearchTerm.toLowerCase().trim();
			if (!searchTerm) {
				this.filteredDataLists = [...this.dataLists];
			} else {
				this.filteredDataLists = this.dataLists.filter(list => 
					list.name.toLowerCase().includes(searchTerm) ||
					(list.description && list.description.toLowerCase().includes(searchTerm))
				);
			}
		},
		toggleSelectAllLists() {
			if (this.allListsSelected) {
				this.sequencesCampaign.selectedDataLists = [];
			} else {
				this.sequencesCampaign.selectedDataLists = this.filteredDataLists.map(list => list.id);
			}
		},
		isDataListSelected(listId) {
			return this.sequencesCampaign.selectedDataLists.includes(listId);
		},
		closeModal() {
			// Reset form data when modal is closed
			this.sequencesCampaign = {
				name: '',
				description: '',
				utm_campaign: '',
				selectedDataLists: []
			};
			this.dataListSearchTerm = '';
			this.filteredDataLists = [...this.dataLists];
		},
		async createSequencesCampaign() {
			if (!this.canCreateCampaign) return;
			
			try {
				this.creatingCampaign = true;
				
				const campaignData = {
					name: this.sequencesCampaign.name.trim(),
					description: this.sequencesCampaign.description.trim(),
					utm_campaign: normalizeUtmCampaign(this.sequencesCampaign.utm_campaign),
					data_list_ids: this.sequencesCampaign.selectedDataLists
				};

				const response = await restAPI.post('/sequenced-campaigns', campaignData);
				
				if (response.data && response.data.id) {
					// Close modal
					const modalElement = document.getElementById('sequencesCampaignModal');
					if (modalElement) {
						const modal = Modal.getInstance(modalElement);
						if (modal) {
							modal.hide();
						}
					}
					
					// Show success message
					this.$swal.fire({
						title: 'Campaign Created!',
						text: `Campaign "${campaignData.name}" has been created successfully.`,
						icon: 'success',
						timer: 2000,
						showConfirmButton: false
					});
					
					// Redirect to edit page
					this.$router.push(`/auto-campaigns/edit/${response.data.id}/sequences`);
				}
				
			} catch (error) {
				console.error('Error creating sequences campaign:', error);
				this.$swal.fire({
					title: 'Error',
					text: error.response?.data?.message || 'Failed to create campaign. Please try again.',
					icon: 'error'
				});
			} finally {
				this.creatingCampaign = false;
			}
		}
	}
}
</script>

<style scoped>
.create-auto-campaign-wrapper {
	min-height: 100vh;
	background-color: #f8f9fa;
}

.create-auto-campaign-page {
	padding: 20px 0;
}

.back-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #ffffff;
	border: 1px solid #dee2e6;
	color: #6c757d;
	text-decoration: none;
	transition: all 0.2s ease;
}

.back-btn:hover {
	background-color: #f8f9fa;
	color: #495057;
	border-color: #adb5bd;
}

.page-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #212529;
	margin-bottom: 0.25rem;
}

.page-subtitle {
	color: #6c757d;
	margin-bottom: 0;
	font-size: 0.95rem;
}

.campaign-type-card {
	cursor: pointer;
	transition: all 0.3s ease;
	margin-bottom: 20px;
}

.campaign-type-card .card {
	border: 2px solid #e9ecef;
	transition: all 0.3s ease;
	height: 100%;
}

.campaign-type-card:hover .card {
	border-color: #007bff;
	box-shadow: 0 4px 15px rgba(0, 123, 255, 0.1);
	transform: translateY(-2px);
}

.campaign-type-card.selected .card {
	border-color: #007bff;
	background-color: #f8f9ff;
	box-shadow: 0 4px 20px rgba(0, 123, 255, 0.15);
}

.campaign-icon {
	margin-bottom: 1rem;
}

.card-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #212529;
	margin-bottom: 1rem;
}

.card-text {
	color: #6c757d;
	line-height: 1.6;
	margin-bottom: 1.5rem;
}

.features-list {
	margin-bottom: 1.5rem;
}

.feature-item {
	margin-bottom: 0.5rem;
	font-size: 0.9rem;
	color: #495057;
}

.selection-indicator {
	border-top: 1px solid #e9ecef;
	padding-top: 1rem;
}

.form-check-input:checked {
	background-color: #007bff;
	border-color: #007bff;
}

.form-check-label {
	font-weight: 500;
	color: #495057;
}

.btn-primary {
	background-color: #ffffff;
	border: 1px solid #dee2e6;
	color: #6c757d;
	font-weight: 500;
	padding: 0.75rem 2rem;
	border-radius: 8px;
	transition: all 0.2s ease;
}

.btn-primary:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.btn-primary:not(:disabled):hover {
	background-color: #f8f9fa;
	color: #495057;
	border-color: #adb5bd;
	transform: translateY(-1px);
}

@media (max-width: 768px) {
	.campaign-type-card {
		margin-bottom: 1rem;
	}
	
	.card-title {
		font-size: 1.1rem;
	}
	
	.campaign-icon i {
		font-size: 3rem !important;
	}
}

/* Modal Styles */
.modal-header {
	border-bottom: 1px solid #e9ecef;
	padding: 1.25rem 1.5rem;
}

.modal-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #212529;
}

.modal-body {
	padding: 1.5rem;
	max-height: 70vh;
	overflow-y: auto;
}

.section-title {
	font-size: 1rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
}

.section-subtitle {
	font-size: 0.875rem;
	color: #6b7280;
	margin-bottom: 0;
}

/* Search Input */
.search-wrapper {
	position: relative;
}

.search-input-wrapper {
	position: relative;
	display: flex;
	align-items: center;
}

.search-icon {
	position: absolute;
	left: 12px;
	color: #9ca3af;
	font-size: 0.875rem;
	z-index: 10;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 35px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 0.875rem;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input:focus {
	outline: none;
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Data Lists Selection */
.data-lists-selection {
	max-height: 400px;
	overflow-y: auto;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 1rem;
	background-color: #f9fafb;
}

.select-all-container {
	border-bottom: 1px solid #e5e7eb;
	padding-bottom: 0.75rem;
	margin-bottom: 1rem;
}

.data-lists-grid {
	display: grid;
	gap: 0.75rem;
}

.data-list-item {
	background-color: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	padding: 1rem;
	transition: all 0.2s ease;
	cursor: pointer;
}

.data-list-item:hover {
	border-color: #3b82f6;
	background-color: #f8faff;
}

.data-list-item.selected {
	border-color: #3b82f6;
	background-color: #eff6ff;
}

.data-list-item .form-check {
	margin-bottom: 0;
}

.data-list-item .form-check-input {
	margin-top: 0;
}

.data-list-card {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-left: 1.5rem;
}

.data-list-icon {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background-color: #e0f2fe;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	color: #0369a1;
	flex-shrink: 0;
}

.data-list-info {
	flex: 1;
	min-width: 0;
}

.data-list-name {
	font-weight: 500;
	color: #111827;
	margin-bottom: 0.25rem;
}

.data-list-meta {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.data-list-count {
	font-size: 0.75rem;
	color: #6b7280;
	font-weight: 500;
}

.data-list-description {
	font-size: 0.75rem;
	color: #9ca3af;
	line-height: 1.4;
}

/* Form Controls */
.form-label {
	font-weight: 500;
	color: #374151;
	margin-bottom: 0.5rem;
}

.form-control {
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 0.75rem;
	font-size: 0.875rem;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Modal Footer */
.modal-footer {
	border-top: 1px solid #e9ecef;
	padding: 1rem 1.5rem;
}

.modal-footer .btn {
	font-weight: 500;
}

/* Loading and Empty States */
.no-data-lists {
	color: #6b7280;
}

.spinner-border-sm {
	width: 1rem;
	height: 1rem;
}

/* Form Check Styling */
.form-check-input:checked {
	background-color: #3b82f6;
	border-color: #3b82f6;
}

.form-check-label {
	cursor: pointer;
	width: 100%;
}
</style>